import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Calories } from 'components/price-and-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { useStoreContext } from 'state/store';
import { useStoreUtils } from 'state/store/hooks/use-store-utils';
import { useUIContext } from 'state/ui';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import {
  ItemPrice,
  ListTileImage,
  PriceAndCaloriesListWrapper,
  TileImage,
  TileInner,
  tileHeaderStyles,
} from './styled';
import { IMenuTileProps, IPriceAndCaloriesProps, ISimplifiedItemOption } from './types';

const ItemCalories = styled.p`
  color: ${Styles.color.grey.two};
  margin-block-end: 0;
`;

const TileDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.8rem 0.5rem;
  text-align: center;
  overflow: ellipsis;

  ${Styles.desktop} {
    padding: 1.3rem 2rem;
  }

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
    margin-block-start: 0.25rem;
    font-size: 0.75rem;
  }
`;

const TileHeader = styled.span`
  ${tileHeaderStyles}
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
`;

const TileListDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-block-start: 0.8rem;
  padding-block-end: 0.8rem;
  padding-inline-start: 0.5rem;
  overflow: ellipsis;
  width: calc(100% - 7rem);

  ${Styles.desktop} {
    width: unset;
    padding: 1.3rem 2rem;
  }

  p {
    color: ${Styles.color.grey.two};
    font-family: ${Styles.fontFamily.body};
    margin-block-start: 0;
    margin-block-end: 0;
    font-size: 0.9375rem;

    ${Styles.desktop} {
      font-size: 0.75rem;
      margin-block-start: 0.25rem;
      margin-block-end: unset;
    }
  }
`;

const TileListHeader = styled.h2`
  ${tileHeaderStyles}
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: 'normal';
  font-size: 1.25rem;
  line-height: 1.2rem;
  text-align: start;

  ${Styles.desktop} {
    text-align: unset;
  }
`;

const StyledTileInner = styled(TileInner)`
  display: flex;
  align-items: center;
  padding: 0.25rem;

  ${Styles.desktop} {
    padding: 0;
    display: block;
  }
`;

const StyledPriceAndCaloriesListWrapper = styled(PriceAndCaloriesListWrapper)`
  margin-block-start: 0.3rem;
`;

const StyledItemPrice = styled.span`
  font-size: 0.9375rem;
  font-family: ${Styles.fontFamily.body};
  color: ${Styles.color.pricing};
  margin-inline-end: 0.3rem;

  ${Styles.desktop} {
    margin-inline-end: 0;
    font-weight: ${Styles.fontWeight.heavy};
    font-size: 1rem;
    font-family: ${Styles.fontFamily.base};
  }
`;

const priceAndCalories = ({
  calories,
  price,
  showStaticMenu,
  type,
}: IPriceAndCaloriesProps & {
  showStaticMenu: boolean;
}): JSX.Element | null => {
  if (type === 'section') {
    return null;
  }

  return (
    <>
      {!showStaticMenu && <ItemPrice>{price}</ItemPrice>}
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </>
  );
};

const priceAndCaloriesList = ({
  calories,
  price,
  showStaticMenu,
  type,
}: IPriceAndCaloriesProps & {
  showStaticMenu: boolean;
}): JSX.Element | null => {
  const shouldRenderPriceAndCalories = (!showStaticMenu && price) || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <StyledPriceAndCaloriesListWrapper>
      {!showStaticMenu && <StyledItemPrice>{price}</StyledItemPrice>}
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </StyledPriceAndCaloriesListWrapper>
  );
};

export const Tile = React.memo(
  ({
    enableMenuList,
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
    itemSize,
    itemUnit,
    itemOptions,
    pfand,
  }: IMenuTileProps) => {
    const enablePriceAndCaloriesInMenu = useFlag(
      LaunchDarklyFlag.ENABLE_PRICE_AND_CALORIES_IN_MENU
    );
    const enableGermanLegalRequirement = useFlag(LaunchDarklyFlag.ENABLE_GERMAN_LEGAL_REQUIREMENT);
    const { store } = useStoreContext();

    const { pathMatches } = useLocationContext();
    const { showStaticMenu } = useMenuContext();

    const [Inner, Image, Description, Header] = enableMenuList
      ? [StyledTileInner, ListTileImage, TileListDescription, TileListHeader]
      : [TileInner, TileImage, TileDescription, TileHeader];

    const priceAndCaloriesContent = enableMenuList
      ? priceAndCaloriesList({ type, price, calories, showStaticMenu })
      : priceAndCalories({ type, price, calories, showStaticMenu });

    const isOnMainMenu = pathMatches(routes.menu);
    const { formatCurrencyForLocale } = useUIContext();
    const { formatMessage } = useIntl();

    const handleOnImageLoaded = () => {
      emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
        sanityId: id,
        isOnMainMenu,
        attributes: [],
      });
    };
    const { prices } = useStoreUtils({
      resetStore: () => {},
      store,
    });
    const [pfandValue, setPfandValue] = useState<number>(0);
    const { getPricingAndAvailability } = useMainMenuContext();
    const pricing = getPricingAndAvailability(id);
    const pfandNoteFlag = !!pricing?.price?.default;
    const numericPrice = pricing?.price?.default ?? pricing?.price?.min ?? 0;
    const computedPrice =
      numericPrice !== 0 ? formatCurrencyForLocale(numericPrice - pfandValue) : price;
    const isCombo = itemOptions?.some(item => item.option && item.option.__typename === 'Combo');
    const pricePerUnitMDP =
      itemSize && itemUnit && numericPrice ? (1 / itemSize) * numericPrice : null;

    useEffect(() => {
      if (itemOptions?.length && prices) {
        const pfandItem = itemOptions.find(
          (item: ISimplifiedItemOption) => item.name?.locale?.toLowerCase() === 'pfand'
        );

        const optionKey = pfandItem?.options?.[0]?._key;

        if (optionKey) {
          const pfand = prices[optionKey];
          setPfandValue(pfand ?? 0);
        }
      }
    }, [itemOptions, prices]);

    return (
      <Inner data-testid="menu-tile-bk">
        <Image
          image={image}
          alt=""
          objectFitContain={objectFitContain}
          $isOnMainMenu={isOnMainMenu}
          onImageLoaded={handleOnImageLoaded}
        />
        <Description>
          <Header
            data-testid={`tile-header-${(name || '').toLowerCase().replace(/\s/g, '')}`}
            id={id}
          >
            {name}
          </Header>

          {enablePriceAndCaloriesInMenu &&
            (enableGermanLegalRequirement && computedPrice ? (
              <StyledPriceAndCaloriesListWrapper>
                <StyledItemPrice>{computedPrice}</StyledItemPrice>
                <Calories>
                  <ItemCalories>{calories}</ItemCalories>
                </Calories>
              </StyledPriceAndCaloriesListWrapper>
            ) : (
              priceAndCaloriesContent
            ))}

          {enableGermanLegalRequirement && price && pricePerUnitMDP && (
            <div data-testid="price-per-unit">
              <StyledItemPrice>
                {formatMessage(
                  { id: 'pricePerUnit' },
                  {
                    price: formatCurrencyForLocale(pricePerUnitMDP),
                    unit: itemUnit,
                  }
                )}
              </StyledItemPrice>
            </div>
          )}

          {enableGermanLegalRequirement && pfandValue > 0 && (
            <div data-testid="pfand-amount-menu-view">
              <StyledItemPrice>
                {formatMessage(
                  { id: 'pfand' },
                  {
                    price: formatCurrencyForLocale(pfandValue),
                  }
                )}
              </StyledItemPrice>
            </div>
          )}
          {enableGermanLegalRequirement && isCombo && pfandNoteFlag && (
            <div data-testid="pfand-note">
              <StyledItemPrice>{pfand?.locale || ''}</StyledItemPrice>
            </div>
          )}
        </Description>
      </Inner>
    );
  }
);
